/**
 * Option / sub-option domain for a coverage's наповнення (B2B flexible-package configurator).
 *
 * A coverage owns a flat list of options ("види допомоги"). Each option carries its own limits and
 * a clinic-class franchise grid, and groups a list of sub-options ("напрямки"/services). There is NO
 * inheritance between an option and its sub-options — every entity holds concrete values. The only
 * cross-level rule is a business invariant enforced in the UI: the sum of a sub-option's additive
 * limits must not exceed the parent option's limit of the same type.
 */
import { Diagnosis } from './diagnosis.model';

/** Limit kinds a limit set can express. An empty `limits[]` means "unlimited" (no cap added). */
export enum CoverageLimitType {
  /** Money cap, in the coverage currency (грн). Additive across sub-options. */
  Price = 'Price',
  /** Count cap (visits, procedures, …). Additive across sub-options. */
  Quantity = 'Quantity',
  /** Co-payment share, percent 0–100. NOT additive. */
  Franchise = 'Franchise',
}

/**
 * Clinic-class grid cell key — classes 1–8, matching the API `ClinicClass` values one-for-one. A partner
 * whose class is `Other` (unknown / unclassified) sits outside the franchise mechanism entirely — no
 * franchise is applied for such a visit — so there is deliberately no `Other` cell. "Apply to every class"
 * is an editing convenience in the dialog, not a stored value, so there is no `All` cell either.
 */
export enum ClinicClassIndex {
  Class1 = 'Class1',
  Class2 = 'Class2',
  Class3 = 'Class3',
  Class4 = 'Class4',
  Class5 = 'Class5',
  Class6 = 'Class6',
  Class7 = 'Class7',
  Class8 = 'Class8',
}

export const CLINIC_CLASS_INDEXES: ClinicClassIndex[] = Object.values(ClinicClassIndex);

/** Numeric clinic class (1–8) behind a grid key. */
export function clinicClassNumber(index: ClinicClassIndex): number {
  const match = /^Class(\d)$/.exec(index);
  return match ? Number(match[1]) : 0;
}

/** The single franchise shared by every cell, or null when they differ — drives the dialog's "all" input. */
export function uniformFranchise(classes: ClinicClass[]): number | null {
  if (classes.length < CLINIC_CLASS_INDEXES.length) return null;
  const first = classes[0].franchise;
  return classes.every(clinic => clinic.franchise === first) ? first : null;
}

export interface CoverageLimit {
  type: CoverageLimitType;
  /** Price/Quantity: absolute value. Franchise: percent 0–100. */
  amount: number;
}

/** One cell of the clinic-class franchise grid: co-payment percent for a clinic class. */
export interface ClinicClass {
  index: ClinicClassIndex;
  /** Co-payment share for this clinic class, percent 0–100. */
  franchise: number;
}

/** Coverage category (dictionary item served from the DB) an option / sub-option is tagged with. */
export interface CoverageCategory {
  /** Unique identifier of the category. */
  code: number;
  nameUa: string;
}

/** Підопція / service under an option. Holds its own limits and categories. */
export interface CoverageSubOption {
  id: string;
  /** Stable identity across coverages, assigned by the backend on creation. Empty for an unsaved row. */
  groupName: string;
  name: string;
  description: string;
  /** Freely picked from the dictionary; every pick is also absorbed into the parent option. */
  categories: CoverageCategory[];
  limits: CoverageLimit[];
}

/** Опція. Holds limits, a clinic-class grid, covered diagnoses and its sub-options. */
export interface CoverageOption {
  id: string;
  /** Stable identity across coverages, assigned by the backend on creation. Empty for an unsaved row. */
  groupName: string;
  name: string;
  description: string;
  /** Categories the option is tagged with: its own picks united with those of its sub-options. */
  categories: CoverageCategory[];
  limits: CoverageLimit[];
  clinicClasses: ClinicClass[];
  /** Lowest-level diagnoses covered by the option (imported from the coverage workbook / a template). */
  diagnoses: Diagnosis[];
  subOptions: CoverageSubOption[];
}

/**
 * Categories ordered alphabetically by their Ukrainian name. The dictionary itself keeps the order the
 * backend serves it in; only the categories already picked on an option / sub-option are sorted.
 */
export function sortCategories(categories: CoverageCategory[]): CoverageCategory[] {
  return [...categories].sort((first, second) => first.nameUa.localeCompare(second.nameUa, 'uk'));
}

/** Union of the given category lists, de-duplicated by `code` and alphabetically ordered. */
export function mergeCategories(...lists: (CoverageCategory[] | undefined)[]): CoverageCategory[] {
  const byCode = new Map<number, CoverageCategory>();

  for (const list of lists) for (const category of list ?? []) byCode.set(category.code, category);

  return sortCategories([...byCode.values()]);
}

/** An option's effective category set: its own picks united with those of its sub-options. */
export function deriveOptionCategories(option: CoverageOption): CoverageCategory[] {
  return mergeCategories(option.categories, ...option.subOptions.map(sub => sub.categories));
}

/** Default clinic-class grid: no co-payment anywhere (0%). */
export function buildDefaultClinicClasses(): ClinicClass[] {
  return CLINIC_CLASS_INDEXES.map(index => ({ index, franchise: 0 }));
}
